import { useCallback, useEffect, useState } from 'react';
import { NoteOut } from '@/services/noteGoApi/noteGoApiSchemas';
import { useAuthStore } from '@/stores/authStore';
import useAlertStore from '@/stores/alertStore';
import { startSecondBrainChatStreaming } from '../services/secondBrainService';
import { SecondBrainChat, SecondBrainMessage } from '../types';
import { SECOND_BRAIN_QUESTION_MIN_LENGTH, SECOND_BRAIN_QUESTION_MAX_LENGTH } from '../config';
import { errorReporter } from '@/services/errorReportingService';

let tempIdCounter = 0;

const generateTemporaryId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  // Fallback with counter to prevent collisions
  tempIdCounter += 1;
  return `temp-${Date.now()}-${tempIdCounter}-${Math.random().toString(36).slice(2, 11)}`;
};

const getAssistantMessageIndex = (chat: SecondBrainChat) => {
  for (let index = chat.messages.length - 1; index >= 0; index -= 1) {
    if (chat.messages[index]?.role === 'assistant') {
      return index;
    }
  }
  return -1;
};

export interface UseSecondBrainChatParams {
  setChatHistory: React.Dispatch<React.SetStateAction<SecondBrainChat[]>>;
  setActiveChatId: (id: string | null) => void;
}

export interface UseSecondBrainChatReturn {
  question: string;
  isStreaming: boolean;
  setQuestion: (question: string) => void;
  startChat: (selectedTags: string[], notes: NoteOut[]) => Promise<void>;
  cancelChat: () => void;
  clearQuestion: () => void;
}

export const useSecondBrainChat = ({
  setChatHistory,
  setActiveChatId,
}: UseSecondBrainChatParams): UseSecondBrainChatReturn => {
  const { userToken } = useAuthStore();
  const showAlert = useAlertStore((state) => state.showAlert);

  const [question, setQuestion] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [abortController, setAbortController] = useState<AbortController | null>(null);

  const updateChatAnswer = useCallback(
    (chatId: string, answer: string) => {
      setChatHistory((previous) =>
        previous.map((chat) => {
          if (chat.id !== chatId) {
            return chat;
          }

          const assistantIndex = getAssistantMessageIndex(chat);
          let messages: SecondBrainMessage[];

          if (assistantIndex === -1) {
            messages = [...chat.messages, { role: 'assistant', content: answer, createdAt: new Date().toISOString() }];
          } else {
            messages = chat.messages.map((message, index) =>
              index === assistantIndex ? { ...message, content: answer } : message,
            );
          }

          return {
            ...chat,
            messages,
            answer,
          };
        }),
      );
    },
    [setChatHistory],
  );

  const markChatErrored = useCallback(
    (chatId: string, message: string) => {
      setChatHistory((previous) => previous.map((chat) => (chat.id === chatId ? { ...chat, error: message } : chat)));
    },
    [setChatHistory],
  );

  const startChat = useCallback(
    async (selectedTags: string[], notes: NoteOut[]) => {
      // Cancel any ongoing request first
      if (abortController) {
        abortController.abort();
        setAbortController(null);
      }

      if (!userToken) {
        showAlert('You need to be signed in to use Second Brain.', 'error');
        return;
      }

      const trimmedQuestion = question.trim();
      if (!trimmedQuestion) {
        showAlert('Ask a question to start a chat.', 'warning');
        return;
      }

      if (trimmedQuestion.length < SECOND_BRAIN_QUESTION_MIN_LENGTH) {
        showAlert(`Question must be at least ${SECOND_BRAIN_QUESTION_MIN_LENGTH} characters.`, 'warning');
        return;
      }

      if (trimmedQuestion.length > SECOND_BRAIN_QUESTION_MAX_LENGTH) {
        showAlert(`Question must be less than ${SECOND_BRAIN_QUESTION_MAX_LENGTH} characters.`, 'warning');
        return;
      }

      if (notes.length === 0) {
        showAlert('No matching notes found. Try different words or adjust your tags.', 'warning');
        return;
      }

      const temporaryChatId = generateTemporaryId();
      const newChat: SecondBrainChat = {
        id: temporaryChatId,
        tags: selectedTags,
        model: null,
        createdAt: new Date().toISOString(),
        messages: [
          { role: 'user', content: trimmedQuestion, createdAt: new Date().toISOString() },
          { role: 'assistant', content: '', createdAt: new Date().toISOString() },
        ],
        contextNotes: notes.map((note) => ({
          id: note.id,
          title: note.title ?? null,
          summary: note.summary ?? null,
          createdAt: note.created_at ?? null,
          tags: note.tags ?? [],
          thumbnailUrl: note.thumbnail_list_url ?? note.thumbnail_url ?? null,
        })),
        question: trimmedQuestion,
        answer: '',
      };

      setChatHistory((previous) => [newChat, ...previous]);
      setActiveChatId(temporaryChatId);
      setIsStreaming(true);

      const controller = new AbortController();
      setAbortController(controller);

      try {
        const { chatId, finalText, chat } = await startSecondBrainChatStreaming({
          token: userToken,
          question: trimmedQuestion,
          tags: selectedTags,
          notes,
          model: null,
          signal: controller.signal,
          onUpdate: (partialAnswer) => {
            updateChatAnswer(temporaryChatId, partialAnswer);
          },
        });

        setAbortController(null);
        setIsStreaming(false);
        setQuestion('');

        const resolvedId = chat?.id ?? chatId ?? temporaryChatId;

        if (chat) {
          setChatHistory((previous) => {
            const filtered = previous.filter((item) => item.id !== temporaryChatId && item.id !== chat.id);
            return [chat, ...filtered];
          });
          setActiveChatId(chat.id);
          return;
        }

        if (finalText) {
          updateChatAnswer(temporaryChatId, finalText);
        }

        if (resolvedId !== temporaryChatId) {
          setChatHistory((previous) =>
            previous.map((item) =>
              item.id === temporaryChatId
                ? {
                    ...item,
                    id: resolvedId,
                  }
                : item,
            ),
          );
          setActiveChatId(resolvedId);
        }
      } catch (error) {
        errorReporter.expected('Second Brain chat failed', error, {
          feature: 'secondBrain',
          operation: 'chat',
        });
        const friendlyMessage = 'Second Brain could not generate an answer. Please try again.';
        if (error instanceof Error && error.name === 'AbortError') {
          showAlert('Second Brain chat cancelled.', 'warning');
        } else if (error instanceof Error && error.message === 'BACKEND_NOT_AVAILABLE') {
          showAlert('Second Brain backend API is not available yet. This feature is still in development.', 'warning');
        } else if (error instanceof Error && error.message.startsWith('SECOND_BRAIN_GENERATION_FAILED')) {
          showAlert(friendlyMessage, 'error');
          markChatErrored(temporaryChatId, friendlyMessage);
        } else {
          showAlert('We could not complete the chat. Please try again.', 'error');
          markChatErrored(temporaryChatId, 'We could not complete the chat. Please try again.');
        }
        setIsStreaming(false);
        setAbortController(null);
      }
    },
    [
      question,
      userToken,
      showAlert,
      updateChatAnswer,
      markChatErrored,
      setChatHistory,
      setActiveChatId,
      abortController,
    ],
  );

  const cancelChat = useCallback(() => {
    if (abortController) {
      abortController.abort();
      setAbortController(null);
      setIsStreaming(false);
    }
  }, [abortController]);

  const clearQuestion = useCallback(() => {
    setQuestion('');
  }, []);

  // Cleanup abort controller on unmount
  useEffect(() => {
    const currentController = abortController;
    return () => {
      currentController?.abort();
    };
  }, [abortController]);

  return {
    question,
    isStreaming,
    setQuestion,
    startChat,
    cancelChat,
    clearQuestion,
  };
};
